import {
  mapQueryTableFilterAttributes,
  queryTableFilterAttributes,
  queryTableFilterSchemas,
  mapQueryTableFilterSchemas,
  mutationReviewViews,
  queryViews,
  mapQueryViews,
  queryViewsListCount,
  mapQueryViewsListCount,
  triggerViewOnDemandScan,
  querySchemaViews,
  mapQuerySchemaViews,
  queryViewCompactClusters,
  mapQueryViewCompactClusters,
  queryViewCompactSchemas,
  mapQueryViewCompactSchemas
} from './viewQueries'
import { DsView, ViewsListCountParams, ViewsListParams } from './viewsType'
import { defaultSortParams, getSortDirection, SortParams } from '../../utils/sortUtil'
import {
  ATTRIBUTE_IDS,
  ATTRIBUTE_SENSITIVITY,
  ATTRIBUTE_SET_IDS,
  DATABASE,
  DATABASE_ID,
  DATA_SOURCE_ID,
  ENTITY_IDS,
  FILTER_PII_VALUES,
  IS_ALL,
  IS_AT_RISK,
  IS_SENSITIVE,
  PAGE,
  PAGE_SIZE,
  // RISK_LEVELS,
  SCHEMA,
  SEARCH_QUERY
} from '../../constants'
import graphqlService from '../../services/graphqlService'
import { RootState } from '../../rootReducer'
import { getGlobalParams } from '../../utils/urlUtil'
import { DropdownLabelItem } from '../documentLabelsV2/types'
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

export enum TableChangeTypes {
  create = 'CREATE',
  update = 'UPDATE',
  delete = 'DELETE'
}

export type ViewCompact = {
  tableId: string
  tableName: string
  schemaId?: string
  columnsCount: number
  rowsCount: number
  parentId: string
  changeType: TableChangeTypes
}

// export type Table = {
//   tableId: string
//   tableName: string
//   columnsSensitiveCount: number
//   columnsCount: number
//   schemaName: string
//   databaseName?: string
//   confidence: number
//   rowsCount: number
//   issuesCount?: number
//   risk?: RISK_LEVELS
//   lastModified?: string
//   modifiedBy?: string
//   isReviewed: boolean
//   changeType: TableChangeTypes
// }

export type TableMetaData = {
  identityTableTimestampColumnID?: string
  primaryIdentifierColumnID?: string
  piiColumnIDsToScan?: string[]
  labels?: DropdownLabelItem[]
}

export type TablesListSettings = {
  list?: DsView[]
  total?: number
  sort: SortParams
}

interface TablesState {
  all: TablesListSettings
  sensitive: TablesListSettings
  issues: TablesListSettings
  viewsListCompact: {
    views?: ViewCompact[]
    viewsTotal?: number
    clusters?: ViewCompactCluster[]
    clustersTotal?: number
    schemas?: ViewCompactSchema[]
    schemasTotal?: number
  }
  filters: {
    attributes?: ViewFilterAttribute[]
    schemas?: ViewFilterSchema[]
  }
  metadata?: TableMetaData
  isMetaDataUpdated: boolean
  isMetaDataSaved: boolean
}

const initialList: TablesListSettings = {
  sort: defaultSortParams
}

export const initialState: TablesState = {
  all: initialList,
  sensitive: initialList,
  issues: initialList,
  viewsListCompact: {},
  filters: {},
  isMetaDataUpdated: false,
  isMetaDataSaved: false
}
export interface TableListsCountParams {
  [DATA_SOURCE_ID]: string
  [DATABASE_ID]?: string
  [SEARCH_QUERY]?: string
  piiDetection?: FILTER_PII_VALUES[]
  filters: {
    [ATTRIBUTE_SET_IDS]?: string
    [ATTRIBUTE_IDS]?: string
    [ATTRIBUTE_SENSITIVITY]?: string
    [DATABASE]?: string
    [SCHEMA]?: string
  }
}

export interface TablesListParams {
  [PAGE]: number
  [PAGE_SIZE]?: number
  [IS_AT_RISK]?: boolean
  [IS_SENSITIVE]?: boolean
  [DATA_SOURCE_ID]: string
  [DATABASE_ID]?: string
  [SEARCH_QUERY]?: string
  includeColumnCounts?: boolean
  piiDetection?: FILTER_PII_VALUES[]
  filters: {
    [ATTRIBUTE_SET_IDS]?: string
    [ENTITY_IDS]?: string
    [ATTRIBUTE_IDS]?: string
    [ATTRIBUTE_SENSITIVITY]?: string
    [DATABASE]?: string
    [SCHEMA]?: string
  }
}

export const ACTION_FETCH_VIEWS_LIST_COUNT = 'views/listCount'
export const fetchViewsListCount = createAsyncThunk(
  ACTION_FETCH_VIEWS_LIST_COUNT,
  async (params: ViewsListCountParams) => {
    const resultRaw = await graphqlService.execute(queryViewsListCount(params))

    return mapQueryViewsListCount(resultRaw)
  }
)

export const ACTION_FETCH_VIEWS_LIST = 'views/fetch'
export const fetchViewsList = createAsyncThunk(
  ACTION_FETCH_VIEWS_LIST,
  async (params: ViewsListParams, { getState }) => {
    const queryParams = { ...params, ...getGlobalParams(getState() as RootState) }

    const resultRaw = await graphqlService.execute(queryViews(queryParams))

    const list = mapQueryViews(resultRaw)

    return { list, ...params }
  }
)

export type ViewFilterAttribute = {
  id: string
  name: string
  internalName: string
  attributeSets: Array<{ id: string; name: string; enabled: boolean }>
}
export const ACTION_VIEWS_FILTER_ATTRIBUTES = 'views/filterAttributes'
export const fetchViewsFilterAttributes = createAsyncThunk(
  ACTION_VIEWS_FILTER_ATTRIBUTES,
  async () => {
    const raw = await graphqlService.execute(queryTableFilterAttributes())
    return mapQueryTableFilterAttributes(raw)
  }
)

export type ViewFilterSchema = {
  id: string
  name: string
  databases: Array<{ id: string; name: string }>
}
export type ViewFilterSchemaParams = {
  [DATA_SOURCE_ID]: string
}
export const ACTION_VIEWS_FILTER_SCHEMAS = 'views/filterSchemas'
export const fetchViewsFilterSchemas = createAsyncThunk(
  ACTION_VIEWS_FILTER_SCHEMAS,
  async (params: ViewFilterSchemaParams) => {
    const raw = await graphqlService.execute(queryTableFilterSchemas(params))
    return mapQueryTableFilterSchemas(raw)
  }
)

export type SchemaViewsParams = {
  [DATA_SOURCE_ID]: string
  [PAGE]: number
  schemaId: string
}

export const ACTION_SCHEMA_VIEWS = 'views/dataSourceSchemaViews'
export const fetchSchemaViews = createAsyncThunk(
  ACTION_SCHEMA_VIEWS,
  async (params: SchemaViewsParams) => {
    const raw = await graphqlService.execute(querySchemaViews(params), ACTION_SCHEMA_VIEWS)
    return mapQuerySchemaViews(raw)
  }
)

export type ViewCompactCluster = {
  clusterId: string
  clusterName: string
}

export interface ViewsListCompactParams {
  [DATA_SOURCE_ID]: string
}

export const ACTION_TABLES_CLUSTERS = 'tables/clusters'
export const fetchViewClusters = createAsyncThunk(
  ACTION_TABLES_CLUSTERS,
  async (params: ViewsListCompactParams) => {
    const raw = await graphqlService.execute(queryViewCompactClusters(params))
    return mapQueryViewCompactClusters(raw)
  }
)

export type ViewCompactSchema = {
  schemaId: string
  schemaName: string
}
export const ACTION_DATA_SOURCE_SCHEMAS = 'views/dataSourceSchemas'
export const fetchDataSourceSchemas = createAsyncThunk(
  ACTION_DATA_SOURCE_SCHEMAS,
  async (params: ViewsListCompactParams) => {
    const raw = await graphqlService.execute(queryViewCompactSchemas(params))
    return mapQueryViewCompactSchemas(raw)
  }
)

export const ACTION_VIEWS_REVIEW = 'views/review'
export const reviewViews = createAsyncThunk(ACTION_VIEWS_REVIEW, async (viewIds: string[]) => {
  return await graphqlService.execute(mutationReviewViews(viewIds))
})

export const ACTION_SCAN_VIEW = 'views/scan'
export const scanView = createAsyncThunk(ACTION_SCAN_VIEW, async (viewId: string) => {
  const res = await graphqlService.execute(triggerViewOnDemandScan(viewId))

  if (!res.triggerTableOnDemandScan.success) {
    throw new Error('scanView failed')
  }
})

const tablesSlice = createSlice({
  name: 'tables',
  initialState,
  reducers: {
    setSort: (state, { payload }) => {
      state[payload.list].sort = getSortDirection(state[payload.list].sort, payload.column)
    },
    resetLists: (state) => {
      state.all = initialState.all
      state.sensitive = initialState.sensitive
      state.issues = initialState.issues
    },
    // resetWidget: (state) => {
    //   state.widget = initialState.widget
    // },
    setTableMetaData: (state, { payload }) => {
      state.metadata = { ...state.metadata, ...payload }
      const hasMeta =
        payload.identityTableTimestampColumnID ||
        payload.primaryIdentifierColumnID ||
        !!payload.piiColumnIDsToScan?.length
          ? true
          : false
      state.isMetaDataUpdated = hasMeta
    },

    resetTableMetaData: (state) => {
      state.metadata = initialState.metadata
    },
    resetSchemaViews: (state) => {
      state.viewsListCompact.views = initialState.viewsListCompact.views
      state.viewsListCompact.viewsTotal = initialState.viewsListCompact.viewsTotal
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchViewsList.fulfilled, (state, { payload }) => {
      if (payload[IS_ALL]) state.all.list = payload.list
      if (payload[IS_AT_RISK]) state.issues.list = payload.list
      if (payload[IS_SENSITIVE]) state.sensitive.list = payload.list
    })
    builder.addCase(fetchViewsListCount.fulfilled, (state, { payload }) => {
      state.all.total = payload.tablesCount
      state.sensitive.total = payload.tablesPiiCount
      state.issues.total = payload.tablesNeedReviewCount
    })
    builder.addCase(fetchViewsFilterAttributes.fulfilled, (state, { payload }) => {
      state.filters.attributes = payload
    })
    builder.addCase(fetchViewsFilterSchemas.fulfilled, (state, { payload }) => {
      state.filters.schemas = payload
    })
    builder.addCase(fetchViewClusters.fulfilled, (state, { payload }) => {
      state.viewsListCompact.clusters = payload.clusters
      state.viewsListCompact.clustersTotal = payload.total
    })
    builder.addCase(fetchSchemaViews.fulfilled, (state, { payload }) => {
      state.viewsListCompact.views = payload.views
      state.viewsListCompact.viewsTotal = payload.total
    })
    builder.addCase(fetchDataSourceSchemas.fulfilled, (state, { payload }) => {
      state.viewsListCompact.schemas = payload.schemas
      state.viewsListCompact.schemasTotal = payload.total
    })
  }
})

export const {
  setSort,
  setTableMetaData,
  resetLists,
  // resetWidget,
  resetSchemaViews,
  resetTableMetaData
} = tablesSlice.actions

export default tablesSlice.reducer
